import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { apiFetch } from "../../lib/apiFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  transformation: "projet de transformation",
  creation: "projet de création",
  innovation: "projet d'innovation",
  restructuration: "projet de restructuration",
  evenementiel: "projet événementiel",
  autre: "projet",
};

const LEVEL_CLASSES = {
  faible: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  moyenne: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
  moyen: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
  elevee: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300",
  eleve: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300",
};

const PRIORITY_CLASSES = {
  haute: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300",
  moyenne: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
  basse: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
};

export const DeliverableShell = ({ title, subtitle, children }) => (
  <Card>
    <CardContent className="p-7 sm:p-9">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1.5 max-w-2xl text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
          )}
        </div>
        <Badge className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300">
          Généré automatiquement
        </Badge>
      </div>
      <div className="mt-7">{children}</div>
    </CardContent>
  </Card>
);

export const OverviewTab = ({ project }) => {
  const brief = project.brief || {};
  const totalBudget = (project.budget_lines || []).reduce((sum, l) => sum + (l.amount || 0), 0);
  const highRisks = (project.risks || []).filter((r) => r.probability === "elevee").length;
  const pendingActions = (project.actions || []).filter((a) => a.status === "a_faire").length;

  const stats = [
    { label: "Durée", value: `${brief.duration_months || 0} mois` },
    { label: "Équipe", value: `${brief.team_size || 0} pers.` },
    { label: "Budget estimé", value: `${totalBudget.toLocaleString("fr-FR")} €` },
    { label: "Risques élevés", value: highRisks },
    { label: "Actions à faire", value: pendingActions },
    { label: "Phases roadmap", value: (project.roadmap_phases || []).length },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-7 sm:p-9">
          <span className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
            {TYPE_LABELS[brief.type] || "Projet"}
          </span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
            {brief.name}
          </h2>
          <p className="mt-3 max-w-2xl text-slate-500 dark:text-slate-400 leading-relaxed">
            {brief.context || "Aucun contexte renseigné."}
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 border-t border-slate-200 dark:border-white/10 pt-7">
            {stats.map((s) => (
              <div key={s.label}>
                <p className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
                  {s.value}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-5 sm:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
              Objectifs
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {brief.objectives || "À compléter."}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
              Critères de succès
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {brief.success_criteria || "À compléter."}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const PowerPointTab = ({ project }) => {
  const brief = project.brief || {};
  const actions = project.actions || [];
  const risks = project.risks || [];
  const [downloading, setDownloading] = useState(false);

  const slides = [
    { title: brief.name || "Titre du projet", body: TYPE_LABELS[brief.type] || "Projet" },
    { title: "Contexte", body: brief.context || "À compléter" },
    { title: "Objectifs", body: brief.objectives || "À compléter" },
    { title: "Périmètre", body: brief.scope || "À compléter" },
    { title: "Roadmap", body: `${(project.roadmap_phases || []).length} phases sur ${brief.duration_months} mois` },
    { title: "Budget", body: `Estimation : ${brief.budget_range || "à définir"}` },
    { title: "Risques principaux", body: risks.map((r) => r.label).join(" · ") || "Aucun" },
    { title: "Prochaines étapes", body: actions.slice(0, 3).map((a) => a.label).join(" · ") || "Aucune" },
  ];

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await apiFetch(`${API}/projects/${project.project_id}/powerpoint`);
      if (!r.ok) {
        let msg = `Erreur ${r.status}`;
        try { const d = await r.json(); msg = d.detail || msg; } catch (_) { /* ignore */ }
        toast.error(msg);
        return;
      }
      const blob = await r.blob();
      const safeName = (brief.name || "presentation").replace(/[^A-Za-z0-9._-]+/g, "_");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PowerPoint téléchargé");
    } catch (e) {
      toast.error(e.message || "Erreur de téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DeliverableShell title="Présentation PowerPoint" subtitle={`${slides.length} diapositives générées`}>
      <div className="mb-4 flex justify-end">
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="download-pptx-btn"
        >
          {downloading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Génération…
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Télécharger le PowerPoint (.pptx)
            </>
          )}
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {slides.map((s, i) => (
          <div
            key={i}
            className="aspect-[4/3] flex flex-col rounded-lg border border-slate-700 bg-slate-900 p-5 text-white"
          >
            <span className="font-mono text-[0.65rem] text-white/40">
              {String(i + 1).padStart(2, "0")} / {slides.length}
            </span>
            <p className="mt-2 text-base font-semibold" style={{ fontFamily: "Outfit" }}>{s.title}</p>
            <p className="mt-2 text-xs text-white/70 leading-relaxed line-clamp-4">{s.body}</p>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const BusinessPlanTab = ({ deliverables }) => {
  const sections = deliverables?.business_plan_sections || [];
  return (
    <DeliverableShell title="Business plan" subtitle={`${sections.length} sections`}>
      <div className="space-y-6">
        {sections.map((s) => (
          <div key={s.title} className="border-b border-slate-200 dark:border-white/10 pb-5 last:border-0">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
              {s.title}
            </h4>
            <p className="mt-2 text-slate-600 dark:text-slate-300 leading-relaxed">{s.content}</p>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const BusinessCaseTab = ({ deliverables }) => {
  const bc = deliverables?.business_case || {};
  const rows = [
    { label: "Problème", value: bc.problem },
    { label: "Solution proposée", value: bc.solution },
    { label: "Bénéfices attendus", value: bc.benefits },
    { label: "Coûts estimés", value: bc.costs },
    { label: "Recommandation", value: bc.recommendation },
  ];
  return (
    <DeliverableShell title="Business case" subtitle="Synthèse décisionnelle">
      <div className="divide-y divide-slate-200 dark:divide-white/10">
        {rows.map((r) => (
          <div key={r.label} className="grid gap-2 py-4 sm:grid-cols-[200px_1fr] sm:gap-6">
            <span className="font-semibold text-slate-900 dark:text-white">{r.label}</span>
            <span className="text-slate-600 dark:text-slate-300">{r.value}</span>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const CadrageTab = ({ project }) => {
  const brief = project.brief || {};
  const rows = [
    { label: "Périmètre", value: brief.scope || "À compléter." },
    { label: "Objectifs", value: brief.objectives || "À compléter." },
    { label: "Contraintes", value: brief.constraints || "Aucune contrainte majeure identifiée." },
    { label: "Parties prenantes", value: brief.key_stakeholders || "À compléter." },
    { label: "Livrables attendus", value: "Note de cadrage, roadmap, budget prévisionnel, plan d'action et supports de pilotage (COPIL/COMEX)." },
  ];
  return (
    <DeliverableShell title="Note de cadrage" subtitle={brief.context}>
      <div className="divide-y divide-slate-200 dark:divide-white/10">
        {rows.map((r) => (
          <div key={r.label} className="grid gap-2 py-4 sm:grid-cols-[200px_1fr] sm:gap-6">
            <span className="font-semibold text-slate-900 dark:text-white">{r.label}</span>
            <span className="text-slate-600 dark:text-slate-300">{r.value}</span>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const SwotTab = ({ project }) => {
  const brief = project.brief || {};
  const quadrants = [
    { label: "Forces", value: brief.strengths, className: "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/5" },
    { label: "Faiblesses", value: brief.weaknesses, className: "border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/5" },
    { label: "Opportunités", value: brief.opportunities, className: "border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/5" },
    { label: "Menaces", value: brief.threats, className: "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/5" },
  ];

  return (
    <DeliverableShell title="Analyse SWOT" subtitle="Forces, faiblesses, opportunités, menaces">
      <div className="grid gap-4 sm:grid-cols-2">
        {quadrants.map((q) => (
          <div key={q.label} className={`rounded-xl border p-5 ${q.className}`}>
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>{q.label}</p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {q.value || "À compléter."}
            </p>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const RoadmapTab = ({ project }) => {
  const brief = project.brief || {};
  const phases = project.roadmap_phases || [];
  const totalMonths = Math.max(
    ...phases.map((p) => (p.start_month || 0) + (p.duration_months || 0)),
    brief.duration_months || 1
  );

  return (
    <DeliverableShell title="Roadmap" subtitle={`${phases.length} phases sur ${brief.duration_months} mois`}>
      <div className="space-y-5">
        {phases.map((phase) => {
          const leftPct = ((phase.start_month || 0) / totalMonths) * 100;
          const widthPct = ((phase.duration_months || 1) / totalMonths) * 100;
          return (
            <div key={phase.id}>
              <div className="flex items-baseline justify-between">
                <p className="font-semibold text-slate-900 dark:text-white">{phase.title}</p>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  Mois {(phase.start_month || 0) + 1} – {(phase.start_month || 0) + (phase.duration_months || 0)}
                </span>
              </div>
              <div className="mt-2 h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-2.5 rounded-full bg-blue-600"
                  style={{ marginLeft: `${leftPct}%`, width: `${Math.max(widthPct, 4)}%` }}
                />
              </div>
              <ul className="mt-2 flex flex-wrap gap-2">
                {(phase.milestones || []).map((m) => (
                  <li key={m} className="rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300">
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </DeliverableShell>
  );
};

export const ActionPlanTab = ({ project }) => {
  const actions = project.actions || [];
  return (
    <DeliverableShell title="Plan d'action" subtitle={`${actions.length} actions identifiées`}>
      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Responsable</th>
              <th className="px-4 py-3">Échéance</th>
              <th className="px-4 py-3">Priorité</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-white/10">
            {actions.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{a.label}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{a.owner}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                  {new Date(a.deadline).toLocaleDateString("fr-FR")}
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PRIORITY_CLASSES[a.priority]}`}>
                    {a.priority}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DeliverableShell>
  );
};

export const BudgetTab = ({ project }) => {
  const lines = project.budget_lines || [];
  const total = lines.reduce((sum, l) => sum + (l.amount || 0), 0);
  return (
    <DeliverableShell title="Budget prévisionnel" subtitle={`Total estimé : ${total.toLocaleString("fr-FR")} €`}>
      <div className="space-y-3">
        {lines.map((line) => {
          const pct = total > 0 ? (line.amount / total) * 100 : 0;
          return (
            <div key={line.id}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-slate-900 dark:text-white">{line.label}</span>
                <span className="text-slate-500 dark:text-slate-400">{line.amount.toLocaleString("fr-FR")} €</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-2 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </DeliverableShell>
  );
};

export const RisksTab = ({ project }) => {
  const risks = project.risks || [];
  return (
    <DeliverableShell title="Analyse des risques" subtitle={`${risks.length} risques identifiés`}>
      <div className="space-y-4">
        {risks.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 dark:border-white/10 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="font-semibold text-slate-900 dark:text-white">{r.label}</p>
              <div className="flex gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${LEVEL_CLASSES[r.probability]}`}>
                  Probabilité : {r.probability}
                </span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${LEVEL_CLASSES[r.impact]}`}>
                  Impact : {r.impact}
                </span>
              </div>
            </div>
            <p className="mt-2.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              <span className="font-medium text-slate-900 dark:text-white">Mitigation : </span>
              {r.mitigation}
            </p>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const GovernanceTab = ({ project }) => {
  const governance = project.governance || [];
  return (
    <DeliverableShell title="Gouvernance" subtitle={`${governance.length} rôles définis`}>
      <div className="grid gap-4 sm:grid-cols-2">
        {governance.map((g) => (
          <div key={g.id} className="rounded-xl border border-slate-200 dark:border-white/10 p-5">
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>{g.role}</p>
            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">{g.name}</p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{g.responsibilities}</p>
          </div>
        ))}
      </div>
    </DeliverableShell>
  );
};

export const FinancingTab = ({ project, deliverables }) => {
  const brief = project.brief || {};
  return (
    <DeliverableShell title="Dossier de financement" subtitle="Synthèse pour financeurs">
      <div className="space-y-5">
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">Présentation du projet</p>
          <p className="mt-1.5 text-slate-600 dark:text-slate-300">{deliverables?.executive_summary}</p>
        </div>
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">Montant recherché</p>
          <p className="mt-1.5 text-slate-600 dark:text-slate-300">{brief.budget_range || "À préciser"}</p>
        </div>
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">Utilisation des fonds</p>
          <p className="mt-1.5 text-slate-600 dark:text-slate-300">
            Répartition détaillée disponible dans le Budget prévisionnel de ce dossier projet.
          </p>
        </div>
      </div>
    </DeliverableShell>
  );
};

export const CopilTab = ({ project }) => {
  const brief = project.brief || {};
  const notes = [
    `Avancement global du ${TYPE_LABELS[brief.type] || "projet"} "${brief.name}".`,
    `Phase actuelle : ${project.status}.`,
    `Points d'attention : ${brief.constraints || "aucun point bloquant identifié à ce jour."}`,
    "Prochaines échéances : voir Plan d'action et Roadmap.",
  ];
  return (
    <DeliverableShell title="Support COPIL" subtitle="Points d'avancement pour le comité de pilotage">
      <ul className="space-y-3">
        {notes.map((n, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-blue-600" />
            <span className="text-slate-600 dark:text-slate-300">{n}</span>
          </li>
        ))}
      </ul>
    </DeliverableShell>
  );
};

export const ComexTab = ({ project }) => {
  const brief = project.brief || {};
  const summary = `Synthèse à destination du COMEX : ${brief.name} avance selon le planning prévu sur ${
    brief.duration_months
  } mois. Budget engagé conforme aux prévisions (${
    brief.budget_range || "à préciser"
  }). Aucun risque majeur non maîtrisé à ce stade. Décision attendue : poursuite du projet selon le plan validé.`;

  return (
    <DeliverableShell title="Support COMEX" subtitle="Synthèse pour le comité exécutif">
      <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{summary}</p>
    </DeliverableShell>
  );
};